import { ownershipTreeWithMobileNodes } from "./mobileAssistantTree";

const nodeId = (node) => String(node?.ID || node?.id || "");
const rowNodeId = (row) => String(row?.treeNodeId || row?.subjectTreeNodeId || row?.subjectNodeId || "");
const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Company types whose shareholders carry a % in them (no % below a PIC, passive holding,
// quoted company, trust or foundation).
export const PERCENT_HOLDER_TYPES = ['Non Quoted Operational Company', 'Operating Company', 'HoldingActive', 'Holding (Active)'];

// THE rule for ownership %s: only the owners of an operating company or active holding hold a
// meaningful % in it. Below a PIC, passive holding, trust or foundation (and for the client's own
// shareholders when the client is one of those, or quoted) the % is "not applicable": it is stored
// empty and never shown. The UBO engines count such a link as 100% themselves, from the parent type.
export function parentCarriesPercent(parent) {
  if (!parent) return false;
  if (parent.isPIC || String(parent.entityCategory || '').toLowerCase() === 'pic') return false;
  return PERCENT_HOLDER_TYPES.includes(String(parent.type || '').trim());
}

export function clientCarriesPercent(ownership) {
  const flags = ownership?.uboFlags || {};
  return !flags.pctNotMeaningful && !flags.quotedCompany && !flags.trustFoundation &&
    !flags.accountHolderTrust && !flags.accountHolderFoundation;
}

// Clears every % that does not apply (see parentCarriesPercent), so none is stored as 100% or
// copied from a parent: the client's shareholders when the client carries none, tree nodes below a
// non-% parent, and controlling persons whose direct parent carries none.
export function withoutInapplicablePercents(ownership) {
  if (!ownership || typeof ownership !== 'object') return ownership;
  const next = { ...ownership };
  const clientPercent = clientCarriesPercent(ownership);
  const tree = ownership.assistantTree;
  const nodes = Array.isArray(tree?.nodes) ? tree.nodes : [];
  const byId = new Map(nodes.map((node) => [nodeId(node), node]));
  const nodeHasPercent = (node) => {
    const parentId = String(node?.parentIDs?.[0] || '');
    if (!parentId) return true;
    return parentId === 'X0' ? clientPercent : parentCarriesPercent(byId.get(parentId));
  };
  if (nodes.length && nodes.some((node) => node?.percent != null && node.percent !== '' && !nodeHasPercent(node))) {
    next.assistantTree = { ...tree, nodes: nodes.map((node) => {
      if (node?.percent == null || node.percent === '' || nodeHasPercent(node)) return node;
      const { percent: _notApplicable, ...rest } = node;
      return rest;
    }) };
  }
  const shareholderKey = Array.isArray(ownership.shareholders) ? 'shareholders' : 'directShareholders';
  if (!clientPercent && Array.isArray(ownership[shareholderKey])) {
    next[shareholderKey] = ownership[shareholderKey].map((row) =>
      String(row?.ownershipPercent ?? '').trim() ? { ...row, ownershipPercent: '' } : row);
  }
  const uboKey = Array.isArray(ownership.ubos) ? 'ubos' : 'controllingPersonsNatural';
  if (Array.isArray(ownership[uboKey])) {
    next[uboKey] = ownership[uboKey].map((row) => {
      const hasPercent = String(row?.ownershipPercent ?? '').trim() || String(row?.effectiveOwnershipPercent ?? '').trim();
      if (!hasPercent) return row;
      const node = byId.get(String(row?.treeNodeId || ''));
      const applies = node ? nodeHasPercent(node)
        : String(row?.relatedShareholderRowId || '').startsWith('__') ? false
        : (row?.relatedShareholderRowId || row?.relatedShareholderNodeId) ? linkedEntityHoldsPercent(row, ownership, tree) : true;
      return applies ? row : { ...row, ownershipPercent: '', effectiveOwnershipPercent: '' };
    });
  }
  return withOwnershipMirrors(next);
}

// Tree node type a shareholder row describes, or "" when the row does not say (e.g. a plain
// "Entity" or "Holding" without its kind). Builder rows are "Entity" + the node type.
export function shareholderRowNodeType(row) {
  const text = (value) => String(value || "").trim().toLowerCase();
  const holderType = text(row?.holderType);
  const entityType = text(row?.shareholderEntityType);
  if (holderType === "individual" || entityType === "individual") return "Individual";
  if (holderType === "trust" || entityType === "trust") return "Trust";
  if (holderType === "foundation" || entityType === "foundation") return "Foundation";
  if (entityType === "pic" || row?.isPIC) return "PIC";
  if (entityType === "quoted company" || entityType === "quoted") return "Quoted Company";
  if (entityType === "holdingpassive" || entityType === "passive holding" || row?.isHoldingPassive) return "HoldingPassive";
  if (entityType === "holdingactive" || entityType === "active holding" || row?.isHoldingActive) return "HoldingActive";
  if (entityType === "non quoted operational company" || entityType === "operating company") return "Non Quoted Operational Company";
  return "";
}

// Whether the company a controlling-person row is linked to (a direct shareholder row, or an
// intermediate node of the diagram) carries % for its owners.
export function linkedEntityHoldsPercent(row, ownership, snapshot) {
  const nodeId = String(row?.relatedShareholderNodeId || "").trim();
  if (nodeId) {
    const node = (snapshot?.nodes || ownership?.assistantTree?.nodes || [])
      .find((n) => String(n?.ID || n?.id || "") === nodeId);
    return !!node && !node.isPIC && PERCENT_HOLDER_TYPES.includes(String(node.type || ""));
  }
  const rowId = String(row?.relatedShareholderRowId || "");
  const rows = Array.isArray(ownership?.shareholders) ? ownership.shareholders : ownership?.directShareholders || [];
  const shareholder = rowId && rows.find((s) => String(s?._rowId || "") === rowId);
  return !!shareholder && PERCENT_HOLDER_TYPES.includes(shareholderRowNodeType(shareholder));
}

export function parseOwnershipPercentage(value) {
  const text = String(value ?? "");
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return null;
  const number = Number(text);
  return Number.isFinite(number) && number >= 0 && number <= 100 ? number : null;
}

export function maximumOwnershipPercentage(snapshot, edge) {
  const siblings = new Map();
  (snapshot?.nodes || []).forEach((node) => {
    if (node.parentIDs?.[0] === edge.parentId && nodeId(node) !== edge.nodeId) {
      const percent = Number(node.percent);
      siblings.set(nodeId(node), Number.isFinite(percent) && percent >= 0 ? percent : 0);
    }
  });
  const total = [...siblings.values()].reduce((sum, value) => sum + value, 0);
  return Math.max(0, Math.round((100 - total) * 1e12) / 1e12);
}

// The single rule for the tree node ID of each ownership row (SH_rev_n / UBO_rev_n for rows
// without a node of their own). Every builder and every consumer of assessment results uses it,
// so a row always maps to the same node.
// - `known`: a snapshot (its nodes), a Set of node IDs, or null. With known IDs, a row's own ID
//   is kept only when it exists among them; with null, every row's own ID is kept.
// - Rows without a kept ID are numbered in row order, skipping IDs already taken, so a generated
//   ID never duplicates another row's.
export function ownershipRowNodeIds(rows, known, prefix) {
  const knownIds = known == null
    ? null
    : known instanceof Set
    ? known
    : new Set((known?.nodes || []).map(nodeId));
  const explicitIds = (rows || []).map(row => {
    const id = rowNodeId(row);
    return id && (!knownIds || knownIds.has(id)) ? id : '';
  });
  const reserved = new Set(explicitIds.filter(Boolean));
  let generated = 0;
  return explicitIds.map(id => {
    if (id) return id;
    while (reserved.has(`${prefix}_${generated}`)) generated++;
    return `${prefix}_${generated++}`;
  });
}

// Older parts of the app keep second copies of the ownership lists (directShareholders,
// controllingPersonsNatural, controllingPersonsLegalArrangements). The tables edit the main lists;
// this copies them over every second copy present, so the two can never drift apart.
export function withOwnershipMirrors(ownership) {
  if (!ownership || typeof ownership !== 'object') return ownership;
  const next = { ...ownership };
  if ('directShareholders' in next && Array.isArray(next.shareholders)) next.directShareholders = next.shareholders;
  if ('controllingPersonsNatural' in next && Array.isArray(next.ubos)) next.controllingPersonsNatural = next.ubos;
  if ('controllingPersonsLegalArrangements' in next && Array.isArray(next.legalArrangements?.arrangements)) {
    next.controllingPersonsLegalArrangements = { ...(next.controllingPersonsLegalArrangements || {}), arrangements: next.legalArrangements.arrangements };
  }
  return next;
}

// The saved tree whose node IDs rows are matched against, or null when the diagram is rebuilt
// from the rows themselves (manual entry, or no saved diagram) — the same choice the builder makes.
export function ownershipKnownNodes(ownership) {
  const tree = ownershipTreeWithMobileNodes(ownership);
  return tree && tree.source !== 'manualEntry' && tree.mermaid && tree.nodes?.length ? tree : null;
}

const arrangementIdRow = (arr) => ({
  treeNodeId: String(arr?._treeArrangementNodeId || arr?.treeNodeId || arr?.subjectTreeNodeId || "").trim(),
});

function arrangementNodeIds(ownership) {
  const list = ownership?.legalArrangements?.arrangements || [];
  return ownershipRowNodeIds(list.map(arrangementIdRow), ownershipKnownNodes(ownership), 'LA_rev');
}

// "% in [company]" field of a trust/foundation held by an operating company or active holding
// (not a direct shareholder: those have their own % in the shareholders table).
export function arrangementPercentageContext(ownership, snapshot, index) {
  const arrangement = ownership?.legalArrangements?.arrangements?.[index];
  if (!arrangement || String(arrangement.source || '') === 'account_holder') return null;
  const id = arrangementNodeIds(ownership)[index];
  const node = snapshot?.nodes?.find((item) => nodeId(item) === id);
  const parent = snapshot?.nodes?.find((item) => nodeId(item) === node?.parentIDs?.[0]);
  if (!node || !parent || nodeId(parent) === 'X0' || parent.isPIC || parent.entityCategory === 'pic' ||
    !PERCENT_HOLDER_TYPES.includes(parent.type)) return null;
  const edge = { nodeId: id, parentId: nodeId(parent) };
  return { edge, value: node.percent ?? '', maximum: maximumOwnershipPercentage(snapshot, edge), parentName: parent.name || 'the related company' };
}

export function controllingPersonPercentageContext(ownership, snapshot, index) {
  const rows = ownership?.ubos || [];
  const row = rows[index];
  if (row?.basisOfControl !== 'Indirect ownership') return null;
  const id = ownershipRowNodeIds(rows, ownershipKnownNodes(ownership), 'UBO_rev')[index];
  const node = snapshot?.nodes?.find((item) => nodeId(item) === id);
  const parent = snapshot?.nodes?.find((item) => nodeId(item) === node?.parentIDs?.[0]);
  if (!node || !parent || parent.isPIC || parent.entityCategory === 'pic' ||
    !PERCENT_HOLDER_TYPES.includes(parent.type)) return null;
  const edge = { nodeId: id, parentId: nodeId(parent) };
  return { edge, value: node.percent ?? '', maximum: maximumOwnershipPercentage(snapshot, edge), parentName: parent.name || 'the related shareholder' };
}

// Edit an edge, not every occurrence of its label. Names and percentages are
// deliberately not used as identifiers: either can repeat in a structure.
export function editOwnershipPercentage(ownership, snapshot, edge, value) {
  const percent = parseOwnershipPercentage(value);
  if (percent === null) throw new Error("Enter a percentage between 0 and 100.");
  const target = snapshot?.nodes?.find((node) => nodeId(node) === edge.nodeId);
  if (!target || target.parentIDs?.[0] !== edge.parentId) {
    throw new Error("This ownership link has changed. Please edit its percentage again.");
  }
  const maximum = maximumOwnershipPercentage(snapshot, edge);
  if (percent > maximum) {
    throw new Error(`The maximum for this ownership percentage is ${maximum}%, so the total at this level does not exceed 100%.`);
  }
  const nodes = snapshot.nodes.map((node) => nodeId(node) === edge.nodeId
    ? { ...node, percent } : { ...node });
  const byId = new Map(nodes.map((node) => [nodeId(node), node]));
  const replaceEdge = (source) => String(source || "").replace(
    new RegExp(`(^|[;\\n\\s])(${escapeRegex(edge.parentId)}\\s*-->)(?:\\s*\\|[^|]*\\|)?\\s*(${escapeRegex(edge.nodeId)})(?=\\s|[([{:;]|$)`, "g"),
    (_, boundary, before, after) => `${boundary}${before}|"${percent}%"| ${after}`
  );
  const next = { ...ownership, assistantTree: {
    ...snapshot, nodes, mermaid: replaceEdge(snapshot.mermaid),
    flowLines: snapshot.flowLines?.map(replaceEdge),
  } };
  const manual = snapshot.source === "manualEntry";
  const shareholderKey = Array.isArray(ownership.shareholders) ? "shareholders" : "directShareholders";
  const uboKey = Array.isArray(ownership.ubos) ? "ubos" : "controllingPersonsNatural";
  const shareholderIds = ownershipRowNodeIds(ownership[shareholderKey] || [], ownershipKnownNodes(ownership), 'SH_rev');
  next[shareholderKey] = (ownership[shareholderKey] || []).map((row, index) => {
    const id = shareholderIds[index];
    if (!byId.has(id)) return row;
    return { ...row, treeNodeId: id,
      ...(id === edge.nodeId ? { ownershipPercent: String(percent) } : {}),
    };
  });
  // Controlling-person rows keep the person's % in their direct parent (the "% in [company]"
  // field), never a cumulated % in the client: only the edited person's row changes.
  const uboIds = ownershipRowNodeIds(ownership[uboKey] || [], ownershipKnownNodes(ownership), 'UBO_rev');
  next[uboKey] = (ownership[uboKey] || []).map((row, index) => {
    const id = uboIds[index];
    if (!byId.has(id)) return row;
    if (id !== edge.nodeId) return { ...row, treeNodeId: id };
    const result = { ...row, treeNodeId: id, effectiveOwnershipPercent: String(percent) };
    if (manual || Object.prototype.hasOwnProperty.call(row, "ownershipPercent")) {
      result.ownershipPercent = result.effectiveOwnershipPercent;
    }
    return result;
  });
  const arrangements = ownership.legalArrangements?.arrangements;
  if (Array.isArray(arrangements)) {
    const ids = arrangementNodeIds(ownership);
    next.legalArrangements = { ...ownership.legalArrangements, arrangements: arrangements.map((arr, index) =>
      ids[index] === edge.nodeId ? { ...arr, ownershipPercent: String(percent) } : arr) };
  }
  return withOwnershipMirrors(next);
}
